import React from "react";
import { RadioGroup } from "@telia/teddy";
import _intersection from "lodash/intersection";
import _size from "lodash/size";
import { getSubscriptionTitles } from "./getSubscriptionTitles";
import { REALM } from "../../../../../../constants";
import { SubscriptionWithGetIT, SubscriptionWithMBB } from "../../../../../../models";
import ScrollToComponent from "../../../../components/components/molecules/ScrollToComponent/ScrollToComponent";
import SubscriptionTextBlock from "../../../../components/components/molecules/SubscriptionTextBlock/SubscriptionTextBlock";
import ExistingGetOfferBubble from "../../molecules/ExistingGetOfferBubble/ExistingGetOfferBubble";
import SubscriptionRadioCard from "../../molecules/SubscriptionCard/SubscriptionRadioCard";
import { calculateHardcodedDiscount } from "../../../../../../shared/discounts";

export interface SubscriptionCardListProps {
    idPrefix?: string;
    isPDP?: boolean;
    numberOfSubscriptions: number;
    onOpenSubscription?: (offeringCode: string, icim?: string) => void;
    priceInfo?: string[];
    scrollToOnOpen?: boolean;
    selectedSubscriptionCode: string;
    showExistingGetOffer?: boolean;
    footerButton?: boolean;
    showOnlyGivenTextTypes?: boolean;
    showTextTypes?: string[];
    showTitles?: boolean;
    subscriptions: SubscriptionWithGetIT[] | SubscriptionWithMBB[];
    isSummerCampaign2026Enabled?: boolean;
    isSummerCampaignJunior2026Enabled?: boolean;
}

const SubscriptionCardList: React.FC<SubscriptionCardListProps> = ({
    idPrefix = "",
    isPDP,
    subscriptions,
    priceInfo = ["pr. md."],
    numberOfSubscriptions,
    selectedSubscriptionCode,
    showExistingGetOffer,
    showTextTypes = ["info"],
    showTitles = true,
    showOnlyGivenTextTypes = false,
    scrollToOnOpen = false,
    onOpenSubscription = (): void => undefined,
    isSummerCampaign2026Enabled,
    footerButton = true,
    isSummerCampaignJunior2026Enabled,
}: SubscriptionCardListProps) => {
    const accordions = subscriptions
        .filter((sub) => !sub.isHidden)
        .map((s: SubscriptionWithGetIT | SubscriptionWithMBB) => {
            const normalPrice = s.monthlyPrice * numberOfSubscriptions;
            const monthlyOfferingDiscount = s.realm === REALM.B2B ? 0 : s.monthlyDiscount || 0;
            const discountedPrice = Math.ceil(
                (s.monthlyPrice - monthlyOfferingDiscount + (numberOfSubscriptions - 1) * (s.monthlyPriceLines || 0)) *
                    (s.priceMultiplier || 1),
            );

            const hardcodedDiscounts = calculateHardcodedDiscount(
                { monthly: { amountSubscription: s.monthlyPrice, amountDiscount: s.monthlyDiscount || 0 } },
                undefined,
                s.firstYearDiscountAmount,
                isSummerCampaign2026Enabled,
                s.offeringCode,
                isSummerCampaignJunior2026Enabled,
            );
            const hardcodedDiscount = hardcodedDiscounts[0];
            const subscriptionWithCampaignDiscount = hardcodedDiscount
                ? { ...s, firstYearDiscountAmount: hardcodedDiscount.value || s.firstYearDiscountAmount }
                : s;
            const { name, title, price, priceStriketrough, striketroughTitle, tsTitle, priceSuffix, isTeliaTrygg } =
                getSubscriptionTitles(
                    normalPrice,
                    discountedPrice,
                    subscriptionWithCampaignDiscount,
                    false,
                    false,
                    priceInfo,
                    isPDP,
                );
            const id = `${idPrefix}-${(s.offeringCode || "").split(".")[0]}`;

            return (
                <SubscriptionRadioCard
                    key={id}
                    isSelected={selectedSubscriptionCode === s.offeringCode}
                    badge={
                        s.flashText &&
                        s.flashText.showOnPdp &&
                        s.offeringCode !== "TELIA_MOBIL_X_RASK_VT" &&
                        s.offeringCode !== "TELIA_MOBIL_X_RASK_VT_2026"
                            ? {
                                  backgroundColor: s.flashText.color,
                                  color: s.flashText.textColor || "#fff",
                                  text: s.flashText.text,
                              }
                            : undefined
                    }
                    // priceDisclaimer={s.priceDisclaimer}
                    firstYearDiscountAmount={s.firstYearDiscountAmount}
                    ribbonText={s.discountInfoLine}
                    discountBadge={hardcodedDiscount?.text}
                    footerButton={footerButton}
                    textBlocks={
                        <div>
                            {(s.doubleData || s.doubleSpeed) && showExistingGetOffer && (
                                <ExistingGetOfferBubble strikethroughTitle={striketroughTitle} title={tsTitle}>
                                    <div>
                                        TV-og internettkunder fra Telia får automatisk{" "}
                                        <strong className="telia-sans-medium">
                                            dobbel {s.dataUnlimited ? "hastighet" : "data"}
                                        </strong>{" "}
                                        på mobilabonnementet!
                                    </div>
                                </ExistingGetOfferBubble>
                            )}
                            {s.textBlocks
                                ?.filter(
                                    (textBlock) =>
                                        (_size(textBlock.textType) === 0 && !showOnlyGivenTextTypes) ||
                                        _size(_intersection(textBlock.textType || [], showTextTypes)) > 0,
                                )
                                .map((textBlock) => (
                                    <SubscriptionTextBlock
                                        headingSize="xs"
                                        headingTag="h3"
                                        subscriptionSpeed={s.dataMaxSpeed}
                                        doubleSpeed={s.doubleSpeed}
                                        key={textBlock.heading}
                                        textBlock={textBlock}
                                        showExistingGetOffer={showExistingGetOffer}
                                    />
                                ))}
                        </div>
                    }
                    name={name}
                    isTeliaTrygg={isTeliaTrygg}
                    title={showTitles ? title : undefined}
                    strikethroughTitle={striketroughTitle}
                    priceSuffix={priceSuffix}
                    price={price}
                    priceStriketrough={priceStriketrough}
                    familyDiscount={monthlyOfferingDiscount}
                    offeringCode={s.offeringCode}
                />
            );
        });

    const selectedSubscriptionId = `${idPrefix}-${(selectedSubscriptionCode || "").split(".")[0]}`;

    return (
        <ScrollToComponent id={selectedSubscriptionId} scrollToOnOpen={scrollToOnOpen}>
            <RadioGroup
                style={{ width: "100%" }}
                value={selectedSubscriptionCode}
                dir="ltr"
                loop
                name={`${idPrefix}-subscription-cards`}
                onValueChange={(offeringCode) => {
                    const selectedSubscription = subscriptions.find((sub) => sub.offeringCode === offeringCode);
                    if (selectedSubscription) {
                        onOpenSubscription(offeringCode, selectedSubscription.icim);
                    }
                }}
                orientation="vertical"
            >
                {accordions}
            </RadioGroup>
        </ScrollToComponent>
    );
};

export default SubscriptionCardList;
